import React from 'react';
import { Sun, Leaf, Truck, Sprout } from 'lucide-react';
import { BRAND, STOCK } from '@/lib/constants';
import Hero from '@/components/Hero';
import SectionHeader from '@/components/SectionHeader';
import Reveal from '@/components/Reveal';
import SquareButton from '@/components/SquareButton';
import Seo from '@/components/Seo';

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Florida SunKissed Mangoes"
        description="Florida SunKissed Mangoes is a produce sourcing & distribution venture of Coastal Capital & AgriCo. We connect homeowners and local growers across South Florida with consumers, retailers, and wholesale buyers."
        keywords="about Florida SunKissed Mangoes, Coastal Capital AgriCo, Florida mango company, South Florida produce sourcing"
        ogImage={STOCK.golden_orchard}
      />

      <Hero
        testId="about-hero"
        eyebrow="Our Story"
        title={<>Rooted in <span className="text-[hsl(var(--c-mango-red))]">South Florida</span>.</>}
        subtitle={`Florida SunKissed Mangoes is a venture of ${BRAND.parentLine.replace('A ', '')}. We connect homeowners and local growers across South Florida with consumers, retailers, and wholesale buyers — bringing premium Florida-grown mangoes directly to market.`}
        block="yellow"
        bgImage={STOCK.golden_orchard}
        rightImage={STOCK.mango_cut}
        imageBlock="red"
        rightAccent="orange"
        leftAccent="leaf"
        ctas={[
          { label: 'Shop The Boxes', to: '/shop', variant: 'orange', testId: 'about-hero-shop' },
          { label: 'Meet The Varieties', to: '/varieties', variant: 'leaf', testId: 'about-hero-varieties' },
        ]}
      />

      <section data-testid="about-story" className="block-cream border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1100px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-[-0.02em]">
              Every summer, South Florida’s neighborhoods and groves erupt with mangoes — Tommy Atkins, Haden, Keitt, Kent, Valencia Pride, Carrie, and dozens more. Backyards overflow. Trees go unharvested.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-0 md:gap-8 items-start">
              <p className="md:col-span-7 text-lg leading-relaxed text-[hsl(var(--c-ink-soft))]">
                We exist to change that. We connect South Florida’s abundant local harvests with the consumers, retailers, restaurants, juice bars, hotels, distributors, and event vendors who want the very best Florida-grown mangoes — in the quantities they need, when they need them.
              </p>
              <figure className="md:col-span-5 mt-8 md:mt-0 relative border-2 border-[hsl(var(--c-ink))] shadow-[6px_6px_0_0_hsl(var(--c-ink))]">
                <img
                  src={STOCK.mango_grove}
                  alt="Mango tree in a South Florida grove"
                  loading="lazy"
                  className="block w-full aspect-[4/3] object-cover"
                />
                <figcaption className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-[hsl(var(--c-ink))]/85 text-[hsl(var(--c-cream))] text-[10px] uppercase tracking-wider font-bold">South Florida grove · peak season</figcaption>
              </figure>
            </div>
          </Reveal>
          <Reveal delay={0.1} variant="scale">
            <div className="mt-12 bg-[hsl(var(--c-ink))] border-2 border-[hsl(var(--c-ink))] p-8 sm:p-12 relative">
              <div className="absolute top-0 left-0 right-0 h-2 bg-[hsl(var(--c-mango-yellow))]" />
              <div className="font-heading text-2xl sm:text-3xl lg:text-4xl leading-tight text-[hsl(var(--c-cream))] italic">
                “From South Florida groves to markets across America — tree-ripened, hand-selected, and worth the wait.”
              </div>
              <div className="mt-5 eyebrow text-[hsl(var(--c-mango-yellow))]">— Florida SunKissed Mangoes</div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-0 md:gap-8 items-start">
              <figure className="md:col-span-5 relative border-2 border-[hsl(var(--c-ink))] order-2 md:order-1 mt-8 md:mt-0">
                <img
                  src={STOCK.mango_closeup}
                  alt="Hand-selected Florida mangoes"
                  loading="lazy"
                  className="block w-full aspect-[4/3] object-cover"
                />
                <div className="absolute top-0 left-0 right-0 h-[5px] bg-[hsl(var(--c-mango-yellow))]" />
              </figure>
              <p className="md:col-span-7 text-lg leading-relaxed text-[hsl(var(--c-ink-soft))] order-1 md:order-2">
                As a venture of Coastal Capital & AgriCo, we combine grower-first relationships with logistics, distribution, and produce expertise. That means honest pricing for growers, reliable supply for buyers, and the freshest possible Florida mangoes for everyone in between.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PHOTO GALLERY STRIP */}
      <section data-testid="about-gallery" aria-label="Photo gallery" className="block-cream-deep border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-16 lg:py-20">
          <Reveal>
            <SectionHeader
              eyebrow="In The Field"
              size="lg"
              title={<>From grove <br className="hidden sm:block" /> to box.</>}
              subtitle="A look at how South Florida mangoes move from local trees to your kitchen, restaurant, or distribution center."
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[hsl(var(--c-ink))]">
            {[
              { src: STOCK.mango_tree, label: 'On the tree' },
              { src: STOCK.grower_hands, label: 'Hand-harvested' },
              { src: STOCK.mango_crate, label: 'Sorted & packed' },
              { src: STOCK.mango_plated, label: 'Served fresh' },
            ].map((g, i) => (
              <Reveal key={g.label} delay={i * 0.05} variant="up">
                <figure className={`group relative aspect-square overflow-hidden ${i < 3 ? 'border-r-2 border-[hsl(var(--c-ink))]' : ''} lg:border-r-2 lg:last:border-r-0 ${i < 2 ? 'border-b-2 border-[hsl(var(--c-ink))] lg:border-b-0' : ''}`}>
                  <img src={g.src} alt={g.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" />
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[hsl(var(--c-ink))]/85 to-transparent">
                    <figcaption className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-white">{g.label}</figcaption>
                  </div>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="block-teal border-t-2 border-[hsl(var(--c-ink))]">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 lg:py-28">
          <Reveal>
            <SectionHeader
              tone="cream"
              eyebrow="What We Stand For"
              size="xl"
              title={<>Local first. <br className="hidden sm:block" /> Seasonal always. <br className="hidden sm:block" /> Honest pricing.</>}
            />
          </Reveal>
          <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-white/30">
            {[
              { icon: Sun, accent: 'bg-[hsl(var(--c-mango-yellow))]', title: 'Tree-ripened sweetness', body: 'Harvested at peak flavor, never picked green.' },
              { icon: Leaf, accent: 'bg-[hsl(var(--c-leaf))]', title: 'Local Florida growers', body: 'Real names, real groves, real partnerships.' },
              { icon: Sprout, accent: 'bg-[hsl(var(--c-mango-orange))]', title: 'Honest pricing', body: 'Fair, transparent rates that respect the work.' },
              { icon: Truck, accent: 'bg-[hsl(var(--c-mango-red))]', title: 'Reliable logistics', body: 'From Palm Beach pickup to nationwide distribution.' },
            ].map(({ icon: Icon, title, body, accent }, i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="bg-white p-7 lg:p-8 text-[hsl(var(--c-ink))] h-full border-r-2 border-b-2 border-[hsl(var(--c-ink))] last:border-r-0 sm:[&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r-2 lg:[&:nth-child(4n)]:border-r-0 lift-on-hover">
                  <div className={`inline-flex h-12 w-12 items-center justify-center ${accent} border-2 border-[hsl(var(--c-ink))]`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-extrabold leading-tight">{title}</h3>
                  <p className="mt-2 text-sm text-[hsl(var(--c-ink-soft))] leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-12 sm:mt-14 flex justify-start">
              <SquareButton to="/shop" variant="yellow" size="lg" testId="about-shop-cta">Shop Mango Boxes</SquareButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
